import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import "./i18n";
import { DashboardLayout } from "./app/components/dashboard/dashboard-layout";
import { DashboardRoutes } from "./app/routes/dashboard";
import PrivateRoute from "./app/routes/privateRoute";
import PublicOnlyRoute from "./app/routes/publicOnlyRoute";
import PersistLogin from "./app/components/authentication/persistLogin";
import { AppRoutes } from "./app/routes/app";
import { HomePage } from "./app/pages/HomePage";
import { NotFound } from "./app/pages/NotFound";
import { SettingsProvider } from "./app/contexts/settings-context";
import AuthVerify from "./app/components/authentication/authVerify";
import SessionExpiredModal from "./app/components/authentication/sessionExpiredDialog";
import { DashboardRoleRoute } from "./app/routes/dashboardRoleRoute";
import { DashboardPageNotFound } from "./app/pages/dashboard/DashboardPageNotFound";
import { Logout } from "./app/pages/authentication/Logout";
import InternetConnectionStatus from "./app/components/authentication/internetConnectionStatus";
import { HelmetProvider } from "react-helmet-async";
import { SetupPassword } from "./app/pages/dashboard/auth/setup-password";
import { AcceptInvitation } from "./app/pages/dashboard/invitations/accept";
import { MicrosoftOAuthCallback } from "./app/pages/authentication/MicrosoftOAuthCallback";
import PlatformBroadcastWrapper from "./app/components/platform-broadcast/PlatformBroadcastWrapper";
import { PublicDeviationCustomerView } from "./app/pages/public/deviations/customerView";
import { PublicReportCustomerView } from "./app/pages/public/reports/customerView";
import { PublicAppointmentConfirm } from "./app/pages/public/appointments/confirm";

function App() {
  useEffect(() => {
    document.body.classList.add("h-full", "bg-gray-100");
  });

  return (
    <HelmetProvider>
      <Routes>
        <Route path="*" element={<NotFound />} />
        <Route element={<PersistLogin />}>
          <Route element={<PlatformBroadcastWrapper />}>
          <Route
            path="/dashboard"
            element={
              <>
                <InternetConnectionStatus />
                <SessionExpiredModal />
                <AuthVerify />
                <PrivateRoute>
                  <SettingsProvider>
                    <DashboardLayout />
                  </SettingsProvider>
                </PrivateRoute>
              </>
            }
          >
            <Route path="/dashboard/*" element={<DashboardPageNotFound />} />
            {DashboardRoutes.map((route, index) => {
              // Helper function to recursively flatten all routes including nested children
              const flattenRoutes = (routes) => {
                const flattened = [];
                routes.forEach((r) => {
                  if (r.children && r.children.length > 0) {
                    // Add parent route if it has an element
                    if (r.element) {
                      flattened.push(r);
                    }
                    // Recursively flatten children
                    flattened.push(...flattenRoutes(r.children));
                  } else {
                    // Leaf route
                    if (r.element) {
                      flattened.push(r);
                    }
                  }
                });
                return flattened;
              };

              // Get all routes including nested children
              const allRoutes = route.children 
                ? (route.element ? [route, ...flattenRoutes(route.children)] : flattenRoutes(route.children))
                : (route.element ? [route] : []);

              return (
                <Route
                  key={index}
                  element={
                    <>
                      <route.layout title={route.title} />
                    </>
                  }
                >
                  {allRoutes.map((r, routeIndex) => {
                    if (!r.element) return null;
                    return (
                      <Route
                        key={`${index}-${routeIndex}-${r.path}`}
                        path={r.path}
                        element={
                          <DashboardRoleRoute
                            role={r.role}
                            roles={r.roles}
                            feature={r.feature}
                            action={r.action}
                            actions={r.actions}
                            requireAdmin={r.requireAdmin}
                            requireAllRoles={r.requireAllRoles}
                            drivingFleetVehiclesList={r.drivingFleetVehiclesList}
                          >
                            <r.element />
                          </DashboardRoleRoute>
                        }
                      />
                    );
                  })}
                </Route>
              );
            })}
          </Route>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route
            path="/deviations/customer/:token"
            element={<PublicDeviationCustomerView />}
          />
          <Route
            path="/reports/customer/:token"
            element={<PublicReportCustomerView />}
          />
          <Route
            path="/appointments/confirm/:token"
            element={<PublicAppointmentConfirm />}
          />
          <Route path="/authentication/logout" element={<Logout />} />
          <Route path="/dashboard/auth/setup-password" element={<SetupPassword />} />
          <Route path="/dashboard/invitations/accept" element={<AcceptInvitation />} />
          <Route
            path="/auth/microsoft/callback"
            element={<MicrosoftOAuthCallback />}
          />
          <Route path="/">
            {AppRoutes.map((route, index) => {
              return (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <PublicOnlyRoute>
                      <route.element />
                    </PublicOnlyRoute>
                  }
                />
              );
            })}
          </Route>
          {/* Public routes END */}
          </Route>
        </Route>
      </Routes>
    </HelmetProvider>
  );
}

export default App;
